/**
 * RequiredDocsPanel — the "Required Documents" card inside DocumentUpload.
 *
 * Shows upload slots for each required doc type, plus a list of docs already
 * uploaded in previous sessions.
 */
import type { NormalisedDoc } from '../utils/doc-normalizer';
import type { SlotState } from './doc-upload-slot';
import { DocUploadSlot } from './doc-upload-slot';
import { docFillsSlot } from '../utils/doc-normalizer';
import { UploadedDocRow } from './uploaded-doc-row';
import { PALETTE_FOREST } from '../tokens';

interface UploadedDoc {
  id:              string;
  document_type:   string;
  document_label?: string | null;
  is_downloadable: boolean;
}

interface StagedFile {
  id:           string;
  documentType: string;
  file:         File;
  state:        SlotState;
  progress:     number;
}

interface RequiredDocsPanelProps {
  requiredDocs:    NormalisedDoc[];
  stagedFiles:     StagedFile[];
  uploadedDocs:    UploadedDoc[];
  isDownloading:   boolean;
  downloadingDocId: string | undefined;
  onFileSelected:  (file: File, documentType: string) => void;
  onRemoveStaged:  (documentType: string) => void;
  onDownload:      (documentId: string) => void;
}

export function RequiredDocsPanel({
  requiredDocs,
  stagedFiles,
  uploadedDocs,
  isDownloading,
  downloadingDocId,
  onFileSelected,
  onRemoveStaged,
  onDownload,
}: RequiredDocsPanelProps) {
  function getSlotState(slot: NormalisedDoc): SlotState {
    const staged = stagedFiles.find(f => f.documentType === slot.document_type);
    if (staged) return staged.state;
    if (uploadedDocs.some(d => docFillsSlot(d, slot))) return 'uploaded';
    return 'idle';
  }

  function getFilename(slot: NormalisedDoc): string | undefined {
    const staged = stagedFiles.find(f => f.documentType === slot.document_type);
    if (staged) return staged.file.name;
    return uploadedDocs.find(d => docFillsSlot(d, slot))?.document_label ?? undefined;
  }

  return (
    <div className="rounded-2xl border border-border bg-background p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-foreground">Required Documents</h3>
        {uploadedDocs.length > 0 && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: `${PALETTE_FOREST}15`, color: PALETTE_FOREST }}
          >
            {uploadedDocs.length} uploaded
          </span>
        )}
      </div>

      {requiredDocs.length > 0 ? (
        <div className="space-y-2">
          {requiredDocs.map(doc => (
            <DocUploadSlot
              key={doc.document_type}
              documentType={doc.document_type}
              state={getSlotState(doc)}
              filename={getFilename(doc)}
              progress={stagedFiles.find(f => f.documentType === doc.document_type)?.progress}
              onFileSelected={onFileSelected}
              onRemove={() => onRemoveStaged(doc.document_type)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground py-3 text-center">
          No specific documents required yet — upload any relevant files below.
        </p>
      )}

      {uploadedDocs.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: PALETTE_FOREST }}>
            Previously uploaded ({uploadedDocs.length})
          </p>
          {uploadedDocs.map(doc => (
            <UploadedDocRow
              key={doc.id}
              doc={doc}
              isDownloading={isDownloading && downloadingDocId === doc.id}
              onDownload={onDownload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
